import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDashboard } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  UsersIcon, PresentationChartBarIcon, CalendarDaysIcon,
  CheckCircleIcon, MegaphoneIcon, UserCircleIcon,
  MapPinIcon, VideoCameraIcon, ClockIcon, FolderOpenIcon,
} from '@heroicons/react/24/outline';

function StatCard({ icon: Icon, label, value, color, to }) {
  const content = (
    <div className={`card flex items-center gap-4 hover:border-gray-700 transition-all ${to ? 'cursor-pointer' : ''}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-gray-500 text-sm">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function StatusBadge({ status }) {
  const map = {
    upcoming: 'bg-blue-900/50 text-blue-300',
    completed: 'bg-green-900/50 text-green-300',
    cancelled: 'bg-red-900/50 text-red-300',
    scheduled: 'bg-purple-900/50 text-purple-300',
  };
  return <span className={`badge ${map[status] || 'bg-gray-800 text-gray-400'}`}>{status}</span>;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ['dashboard'], queryFn: getDashboard });
  const d = data?.data;

  if (isLoading) return <div className="text-gray-400 text-sm">Loading dashboard...</div>;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const nextMeeting = d?.next_meeting;
  const nextMeetingDate = nextMeeting ? new Date(nextMeeting.meeting_date) : null;
  const daysUntil = nextMeetingDate
    ? formatDistanceToNow(nextMeetingDate, { addSuffix: true })
    : null;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-white">{greeting}, {user?.full_name?.split(' ')[0]} 👋</h1>
        <p className="text-gray-500 text-sm mt-1">Here's what's happening in the THRU Zim Fellowship today.</p>
      </div>

      {/* Next Meeting Banner */}
      {nextMeeting && (
        <div className="rounded-xl border border-primary-700/50 bg-primary-600/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-600/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CalendarDaysIcon className="w-5 h-5 text-primary-300" />
            </div>
            <div>
              <div className="text-xs text-primary-400 font-semibold uppercase tracking-wider mb-0.5">Next Meeting</div>
              <div className="text-white font-semibold">{nextMeeting.title}</div>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <ClockIcon className="w-3.5 h-3.5" />
                  {format(new Date(nextMeeting.meeting_date), 'EEEE, dd MMM yyyy')} at {format(new Date(nextMeeting.meeting_date), 'HH:mm')}
                  <span className="text-primary-400 font-medium ml-1">({daysUntil})</span>
                </span>
                {nextMeeting.venue && (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    {nextMeeting.venue}
                  </span>
                )}
              </div>
            </div>
          </div>
          {nextMeeting.zoom_link && (
            <a
              href={nextMeeting.zoom_link}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
            >
              <VideoCameraIcon className="w-4 h-4" />
              Join Zoom
            </a>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { to: '/presentations', icon: PresentationChartBarIcon, label: 'Presentations', sub: 'Add or view yours', color: 'bg-purple-600/20 text-purple-400 border-purple-800/50' },
          { to: `/fellows/${user?.id}`, icon: UserCircleIcon, label: 'My Profile', sub: 'Update your details', color: 'bg-primary-600/20 text-primary-400 border-primary-800/50' },
          { to: '/meetings', icon: CalendarDaysIcon, label: 'Meetings', sub: 'See schedule', color: 'bg-green-600/20 text-green-400 border-green-800/50' },
          { to: '/resources', icon: FolderOpenIcon, label: 'Resources', sub: 'Guides & templates', color: 'bg-amber-600/20 text-amber-400 border-amber-800/50' },
        ].map(({ to, icon: Icon, label, sub, color }) => (
          <Link key={to} to={to} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border text-center hover:opacity-80 transition-all ${color}`}>
            <Icon className="w-6 h-6" />
            <div>
              <div className="text-sm font-semibold text-white">{label}</div>
              <div className="text-xs text-gray-500">{sub}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={UsersIcon} label="Active Fellows" value={d?.total_fellows ?? 0} color="bg-primary-600" to="/fellows" />
        <StatCard icon={PresentationChartBarIcon} label="Upcoming Presentations" value={d?.active_presentations ?? 0} color="bg-purple-600" to="/presentations" />
        <StatCard icon={CalendarDaysIcon} label="Upcoming Meetings" value={d?.upcoming_meetings ?? 0} color="bg-green-600" to="/meetings" />
        <StatCard icon={CheckCircleIcon} label="My Pending Tasks" value={d?.pending_tasks ?? 0} color="bg-amber-600" to="/tasks" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Announcements */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <MegaphoneIcon className="w-4 h-4 text-primary-400" />
              Latest Announcements
            </h2>
            <Link to="/announcements" className="text-xs text-primary-400 hover:text-primary-300">View all</Link>
          </div>
          {d?.recent_announcements?.length === 0 ? (
            <p className="text-gray-500 text-sm">No announcements yet.</p>
          ) : (
            <div className="space-y-3">
              {d?.recent_announcements?.map((ann) => (
                <div key={ann.id} className="border-l-2 border-primary-600 pl-3">
                  {ann.is_pinned && <span className="badge bg-amber-900/50 text-amber-300 text-xs mb-1">Pinned</span>}
                  <div className="text-sm font-medium text-gray-200">{ann.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {ann.author?.full_name} · {ann.category} · {format(new Date(ann.created_at), 'dd MMM')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Presentations */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <PresentationChartBarIcon className="w-4 h-4 text-purple-400" />
              Upcoming Presentations
            </h2>
            <Link to="/presentations" className="text-xs text-primary-400 hover:text-primary-300">View all</Link>
          </div>
          {d?.upcoming_presentations?.length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming presentations.</p>
          ) : (
            <div className="space-y-3">
              {d?.upcoming_presentations?.map((p) => (
                <div key={p.id} className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-200">{p.title}</div>
                    <div className="text-xs text-gray-500">{p.presenter?.full_name}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-gray-300">{format(new Date(p.presentation_date), 'dd MMM')}</div>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Meetings */}
        <div className="card lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <CalendarDaysIcon className="w-4 h-4 text-green-400" />
              Upcoming Meetings
            </h2>
            <Link to="/meetings" className="text-xs text-primary-400 hover:text-primary-300">View all</Link>
          </div>
          {d?.upcoming_meetings_list?.length === 0 ? (
            <p className="text-gray-500 text-sm">No upcoming meetings.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {d?.upcoming_meetings_list?.map((m) => (
                <div key={m.id} className="bg-surface-100 rounded-lg px-4 py-3">
                  <div className="text-sm font-semibold text-white">{m.title}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {format(new Date(m.meeting_date), 'dd MMM yyyy, HH:mm')}
                  </div>
                  {m.zoom_link && (
                    <a href={m.zoom_link} target="_blank" rel="noreferrer" className="text-xs text-primary-400 hover:underline mt-1 inline-block">
                      Join Meeting
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
